'use client';

import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  X,
  Sparkles,
  Send,
  Bot,
  CheckCircle2,
  RotateCcw,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { useAuth } from '@/hooks';

interface AgentAction {
  type: 'scroll' | 'search' | 'modality' | 'navigate' | 'click' | 'triage';
  label: string;
  target?: string;
  query?: string;
  modality?: 'video' | 'office' | 'home';
}

interface AgentMessage {
  id: string;
  sender: 'agent' | 'user';
  text: string;
  time: string;
  actionExecuted?: AgentAction;
  actionStatus?: 'thinking' | 'executing' | 'completed';
  suggestions?: string[];
}

export interface FloatingHelpButtonProps {
  className?: string;
}

export function FloatingHelpButton({ className }: FloatingHelpButtonProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const userRole = user?.role || 'GUEST';

  const [isOpen, setIsOpen] = useState(false);
  const [inputText, setInputText] = useState('');
  const [isAgentBusy, setIsAgentBusy] = useState(false);
  const [agentActivity, setAgentActivity] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Dynamic quick prompts based on role and active route
  const quickPrompts = useMemo(() => {
    if (userRole === 'DOCTOR') {
      return [
        { label: 'Fila Expressa (Plantão)', query: 'Quero ver a fila de espera do plantão express' },
        { label: 'Emitir Nova Receita', query: 'Quero emitir uma nova receita médica digital' },
        { label: 'Minhas Prescrições', query: 'Ver minhas prescrições emitidas' },
        { label: 'Meus Pacientes', query: 'Consultar lista de pacientes atendidos' },
        { label: 'Minha Disponibilidade', query: 'Configurar horários de atendimento e agenda' },
      ];
    }

    if (userRole === 'PATIENT') {
      return [
        { label: 'Minhas Receitas', query: 'Quero ver e baixar minhas receitas médicas digitais' },
        { label: 'Plantão 24h Agora', query: 'Preciso de médico agora por vídeo no plantão 24h' },
        { label: 'Meus Exames', query: 'Ver meus pedidos de exames laboratoriais' },
        { label: 'Meus Atestados', query: 'Acessar meus atestados médicos' },
        { label: 'Histórico de Consultas', query: 'Ver meu histórico de consultas' },
      ];
    }

    // Default Guest Prompts
    return [
      { label: 'Plantão 24h Agora', query: 'Preciso de médico agora por vídeo no plantão 24h' },
      { label: 'Buscar Clínico Geral', query: 'Quero agendar consulta com Clínico Geral hoje' },
      { label: 'Médico em Casa', query: 'Como funciona o atendimento médico em domicílio?' },
      { label: 'Receita Digital MeMed', query: 'Como funciona a receita digital com QR Code?' },
      { label: 'Valores e Planos', query: 'Quais são os preços das consultas e convênios aceitos?' },
    ];
  }, [userRole]);

  // Initial welcome message tailored to role
  const initialGreeting = useMemo(() => {
    const firstName = user?.name ? user.name.split(' ')[0] : '';
    if (userRole === 'DOCTOR') {
      return `Olá, Dr(a). ${firstName || 'Doutor(a)'}! Sou seu Copiloto Inteligente Medikei. Posso abrir sua fila do plantão, iniciar emissão de receitas MeMed, consultar seus pacientes ou ajustar sua disponibilidade na agenda. Como posso ajudar?`;
    }
    if (userRole === 'PATIENT') {
      return `Olá, ${firstName || 'Paciente'}! Sou o Assistente Inteligente da Medikei. Diga-me o que precisa ou seus sintomas, e eu controlo a tela para você: localizo suas receitas com PDF assinado, exames, atestados ou te levo direto ao Plantão 24h!`;
    }
    return 'Olá! Sou o Agente Inteligente da Medikei. Diga-me o que você precisa ou seus sintomas, e eu controlo a página para você: busco médicos, aplico filtros de agenda, mostro os preços ou te levo direto ao Plantão 24h!';
  }, [userRole, user?.name]);

  const [messages, setMessages] = useState<AgentMessage[]>([
    {
      id: 'welcome',
      sender: 'agent',
      text: initialGreeting,
      time: new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
      suggestions: quickPrompts.slice(0, 4).map((p) => p.label),
    },
  ]);

  // Update welcome message if user changes (e.g. login/logout)
  useEffect(() => {
    setMessages([
      {
        id: 'welcome',
        sender: 'agent',
        text: initialGreeting,
        time: new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
        suggestions: quickPrompts.slice(0, 4).map((p) => p.label),
      },
    ]);
  }, [initialGreeting, quickPrompts]);

  // Outside click and ESC handler
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // Auto-scroll chat log
  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isAgentBusy, isOpen]);

  /**
   * Autonomous In-Page GUI Agent Execution Engine (Page Agent W3C Standard)
   * Directly interacts with live DOM elements on the screen.
   */
  const executeInPageAction = async (action: AgentAction) => {
    setAgentActivity(`Executando ação: ${action.label}...`);

    switch (action.type) {
      case 'navigate': {
        if (action.target) {
          navigate(action.target);
        }
        break;
      }

      case 'click': {
        if (action.target) {
          const candidates = Array.from(document.querySelectorAll('button, a[role="button"]'));
          const targetEl = candidates.find((el) => {
            const text = (el.textContent || '').toLowerCase();
            const aria = (el.getAttribute('aria-label') || '').toLowerCase();
            const needle = action.target!.toLowerCase();
            return text.includes(needle) || aria.includes(needle);
          }) as HTMLElement | null;

          if (targetEl) {
            targetEl.classList.add('ring-4', 'ring-primary/60', 'transition-all', 'duration-300');
            targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            await new Promise((r) => setTimeout(r, 400));

            // W3C Pointer / Mouse / Focus sequence
            targetEl.dispatchEvent(new PointerEvent('pointerdown', { bubbles: true }));
            targetEl.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
            targetEl.focus();
            targetEl.click();

            setTimeout(() => {
              targetEl.classList.remove('ring-4', 'ring-primary/60');
            }, 2500);
          }
        }
        break;
      }

      case 'scroll': {
        if (action.target) {
          const el = document.querySelector(action.target);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            el.classList.add('ring-4', 'ring-blue-500/40', 'transition-all', 'duration-300');
            setTimeout(() => {
              el.classList.remove('ring-4', 'ring-blue-500/40');
            }, 2500);
          }
        }
        break;
      }

      case 'search': {
        // Find visible active text or search inputs on current page
        const searchInput = document.querySelector(
          'input[type="search"], input[placeholder*="Buscar"], input[placeholder*="buscar"], input[placeholder*="Especialidade"], input[placeholder*="Ex: Clínico Geral"]'
        ) as HTMLInputElement | null;

        if (searchInput && action.query) {
          const setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
          if (setter) {
            setter.call(searchInput, action.query);
          } else {
            searchInput.value = action.query;
          }
          searchInput.dispatchEvent(new Event('input', { bubbles: true }));
          searchInput.dispatchEvent(new Event('change', { bubbles: true }));
          searchInput.focus();
          searchInput.classList.add('ring-4', 'ring-primary/60', 'transition-all');
          searchInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setTimeout(() => {
            searchInput.classList.remove('ring-4', 'ring-primary/60');
          }, 2500);
        } else if (action.query) {
          if (userRole === 'DOCTOR') {
            navigate(`/medico/pacientes?search=${encodeURIComponent(action.query)}`);
          } else {
            navigate(`/buscar?q=${encodeURIComponent(action.query)}`);
          }
        }
        break;
      }

      case 'modality': {
        if (action.modality) {
          const modalityButtons = Array.from(document.querySelectorAll('button'));
          const targetText =
            action.modality === 'home'
              ? 'Médico em Casa'
              : action.modality === 'office'
              ? 'Consulta Presencial'
              : 'Telemedicina';

          const btn = modalityButtons.find((b) => b.innerText.includes(targetText));
          if (btn) {
            btn.click();
            btn.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
        break;
      }
    }

    setAgentActivity(null);
  };

  /**
   * Process Natural Language Prompt and determine appropriate GUI Action & Response
   */
  const handleUserSubmit = async (promptToSend?: string) => {
    const text = (promptToSend || inputText).trim();
    if (!text || isAgentBusy) return;

    const userMsg: AgentMessage = {
      id: Date.now().toString(),
      sender: 'user',
      text,
      time: new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInputText('');
    setIsAgentBusy(true);
    setAgentActivity('Analisando intenção e elementos da página...');

    const lower = text.toLowerCase();
    await new Promise((r) => setTimeout(r, 550));

    let replyText = '';
    let plannedAction: AgentAction | undefined;
    let suggestions: string[] | undefined;

    // Red Flag / Emergency Check (Universal)
    if (
      lower.includes('peito') ||
      lower.includes('infarto') ||
      lower.includes('falta de ar') ||
      lower.includes('desmaio') ||
      lower.includes('pressão 2') ||
      lower.includes('avc')
    ) {
      replyText =
        '⚠️ ATENÇÃO MÉDICA: Seus sintomas podem indicar uma emergência médica aguda! Se for um caso grave, ligue imediatamente para o SAMU (192) ou dirija-se à UPA/Pronto-Socorro mais próximo. Para falar com um médico agora mesmo sem fila, estou te direcionando ao Plantão Online 24h.';
      plannedAction = {
        type: 'navigate',
        target: '/expressa',
        label: 'Acessando Plantão Online 24h de Urgência',
      };
      suggestions = ['Acessar Plantão 24h Agora', 'Chamar SAMU 192'];
    }
    // DOCTOR LOGGED-IN COMMANDS
    else if (userRole === 'DOCTOR') {
      if (lower.includes('fila') || lower.includes('plantão') || lower.includes('plantao') || lower.includes('chamar') || lower.includes('espera')) {
        replyText = 'Abrindo a sua Fila de Espera do Plantão Express. Lá você pode gerenciar chamadas de vídeo ao vivo e aceitar novos pacientes!';
        plannedAction = {
          type: 'navigate',
          target: '/medico/fila',
          label: 'Navegando para Fila do Plantão Express',
        };
        suggestions = ['Ver Fila do Plantão', 'Ver Minhas Prescrições'];
      } else if (lower.includes('nova receita') || lower.includes('emitir receita') || lower.includes('prescrever') || lower.includes('receita nova')) {
        replyText = 'Abrindo o emissor de nova receita digital integrado à MeMed com certificado ICP-Brasil e catálogo Anvisa.';
        plannedAction = {
          type: 'navigate',
          target: '/medico/receitas/novo',
          label: 'Abrindo Emissor de Nova Receita MeMed',
        };
        suggestions = ['Emitir Nova Receita', 'Ver Histórico de Prescrições'];
      } else if (lower.includes('receita') || lower.includes('prescrição') || lower.includes('prescricao')) {
        replyText = 'Acessando sua lista de prescrições e receitas médicas emitidas.';
        plannedAction = {
          type: 'navigate',
          target: '/medico/receitas',
          label: 'Acessando Prescrições Emitidas',
        };
        suggestions = ['Emitir Nova Receita', 'Ver Pacientes'];
      } else if (lower.includes('paciente') || lower.includes('prontuário') || lower.includes('prontuario')) {
        replyText = 'Abrindo sua lista de pacientes atendidos e histórico de prontuários clínicos.';
        plannedAction = {
          type: 'navigate',
          target: '/medico/pacientes',
          label: 'Acessando Lista de Pacientes',
        };
        suggestions = ['Ver Meus Pacientes', 'Nova Receita'];
      } else if (lower.includes('agenda') || lower.includes('horário') || lower.includes('horario') || lower.includes('disponibilidade')) {
        replyText = 'Levando você à tela de configuração de agenda, horários de atendimento e modalidades habilitadas.';
        plannedAction = {
          type: 'navigate',
          target: '/medico/disponibilidade',
          label: 'Configurando Disponibilidade e Agenda',
        };
        suggestions = ['Ajustar Horários', 'Ver Fila do Plantão'];
      } else if (lower.includes('financeiro') || lower.includes('saldo') || lower.includes('extrato') || lower.includes('saque') || lower.includes('pix')) {
        replyText = 'Acessando seu painel financeiro para consulta de extrato de consultas, repasses e solicitação de saques via PIX.';
        plannedAction = {
          type: 'navigate',
          target: '/medico/financeiro',
          label: 'Acessando Painel Financeiro e Saques',
        };
        suggestions = ['Solicitar Saque PIX', 'Ver Fila do Plantão'];
      } else {
        replyText = 'Entendido, Doutor(a)! Posso te guiar até a fila do plantão, emissão de receitas MeMed, histórico de prontuários ou ajustes na sua agenda. O que deseja fazer?';
        plannedAction = {
          type: 'navigate',
          target: '/medico/dashboard',
          label: 'Acessando Painel do Médico',
        };
        suggestions = ['Fila do Plantão', 'Emitir Receita', 'Minha Agenda'];
      }
    }
    // PATIENT LOGGED-IN COMMANDS
    else if (userRole === 'PATIENT') {
      if (lower.includes('receita') || lower.includes('remédio') || lower.includes('medicamento')) {
        if (location.pathname === '/receitas') {
          replyText = 'Você já está na tela de receitas! Estou clicando em "Verificar Novas Receitas" para atualizar suas prescrições assinadas na MeMed.';
          plannedAction = {
            type: 'click',
            target: 'Verificar Novas Receitas',
            label: 'Atualizando receitas assinadas',
          };
          suggestions = ['Baixar última receita', 'Ver Meus Atestados'];
        } else {
          replyText = 'Acessando sua área de receitas médicas digitais. Lá você pode visualizar e baixar PDFs com QR Code assinados digitalmente.';
          plannedAction = {
            type: 'navigate',
            target: '/receitas',
            label: 'Abrindo tela de Minhas Receitas',
          };
          suggestions = ['Ver Minhas Receitas', 'Ver Meus Atestados'];
        }
      } else if (lower.includes('atestado') || lower.includes('repouso') || lower.includes('afastamento')) {
        replyText = 'Acessando seus atestados médicos homologados pela Medikei com certificado ICP-Brasil.';
        plannedAction = {
          type: 'navigate',
          target: '/atestados',
          label: 'Abrindo tela de Meus Atestados',
        };
        suggestions = ['Ver Meus Atestados', 'Ver Minhas Receitas'];
      } else if (lower.includes('exame') || lower.includes('laboratório') || lower.includes('laboratorio')) {
        replyText = 'Acessando seus pedidos de exames laboratoriais e cotações de coleta domiciliar.';
        plannedAction = {
          type: 'navigate',
          target: '/exames',
          label: 'Abrindo tela de Meus Exames',
        };
        suggestions = ['Ver Pedidos de Exames', 'Plantão 24h'];
      } else if (lower.includes('plantão') || lower.includes('plantao') || lower.includes('urgência') || lower.includes('urgente') || lower.includes('agora')) {
        replyText = 'Direcionando você para o Plantão Online 24h com Clínico Geral em até 6 minutos!';
        plannedAction = {
          type: 'navigate',
          target: '/expressa',
          label: 'Acessando Plantão Online 24h',
        };
        suggestions = ['Entrar na Fila Imediata', 'Histórico de Consultas'];
      } else if (lower.includes('histórico') || lower.includes('historico') || lower.includes('passada') || lower.includes('gravação')) {
        replyText = 'Abrindo seu histórico unificado de consultas anteriores com prontuários e receitas integradas.';
        plannedAction = {
          type: 'navigate',
          target: '/historico',
          label: 'Acessando Histórico de Consultas',
        };
        suggestions = ['Ver Histórico', 'Minhas Receitas'];
      } else if (lower.includes('carteira') || lower.includes('saldo') || lower.includes('pagamento') || lower.includes('pix')) {
        replyText = 'Abrindo sua carteira digital para consulta de saldo, extratos e métodos de pagamento cadastrados.';
        plannedAction = {
          type: 'navigate',
          target: '/carteira',
          label: 'Acessando Carteira Digital',
        };
        suggestions = ['Ver Saldo e Extrato', 'Agendar Consulta'];
      } else {
        replyText = 'Posso te ajudar a acessar suas receitas, atestados, pedidos de exames, histórico de consultas ou te levar ao Plantão 24h. O que precisa no momento?';
        plannedAction = {
          type: 'navigate',
          target: '/dashboard',
          label: 'Abrindo Painel do Paciente',
        };
        suggestions = ['Minhas Receitas', 'Meus Exames', 'Plantão 24h'];
      }
    }
    // GUEST / LANDING COMMANDS
    else {
      if (lower.includes('plantao') || lower.includes('plantão') || lower.includes('agora') || lower.includes('imediato') || lower.includes('urgente')) {
        replyText = 'Temos Clínicos Gerais de prontidão agora por vídeo no Plantão 24h com tempo médio de espera de 6 minutos!';
        plannedAction = {
          type: 'navigate',
          target: '/expressa',
          label: 'Acessando Plantão 24h Online',
        };
        suggestions = ['Entrar na Fila Imediata', 'Ver Especialidades'];
      } else if (lower.includes('casa') || lower.includes('domicilio') || lower.includes('domiciliar') || lower.includes('visita')) {
        replyText = 'O serviço de Médico em Casa envia um profissional com maleta de exames até sua residência a partir de R$ 400. Mostrando a modalidade na vitrine!';
        plannedAction = {
          type: 'modality',
          modality: 'home',
          label: 'Alterando modalidade para "Médico em Casa"',
        };
        suggestions = ['Solicitar Médico em Casa', 'Ver Preços'];
      } else if (
        lower.includes('cardio') ||
        lower.includes('pediatra') ||
        lower.includes('derma') ||
        lower.includes('psico') ||
        lower.includes('gineco') ||
        lower.includes('orto') ||
        lower.includes('psiqui') ||
        lower.includes('clinico') ||
        lower.includes('clínico')
      ) {
        let spec = 'Clínico Geral';
        if (lower.includes('cardio')) spec = 'Cardiologia';
        if (lower.includes('pediatra')) spec = 'Pediatria';
        if (lower.includes('derma')) spec = 'Dermatologia';
        if (lower.includes('psico')) spec = 'Psicologia';
        if (lower.includes('gineco')) spec = 'Ginecologia e Obstetrícia';
        if (lower.includes('orto')) spec = 'Ortopedia';
        if (lower.includes('psiqui')) spec = 'Psiquiatria';

        replyText = `Localizei especialistas em ${spec} com agenda aberta. Preenchendo a busca e exibindo a grade de horários!`;
        plannedAction = {
          type: 'search',
          query: spec,
          label: `Filtrando médicos de ${spec}`,
        };
        suggestions = [`Ver agenda de ${spec}`, 'Plantão 24h'];
      } else if (lower.includes('receita') || lower.includes('memed') || lower.includes('atestado') || lower.includes('remedio') || lower.includes('medicamento')) {
        replyText = 'Nossas prescrições e atestados são emitidos oficialmente com assinatura digital ICP-Brasil e QR Code válido em todas as farmácias do Brasil.';
        plannedAction = {
          type: 'scroll',
          target: '#como-funciona',
          label: 'Rolando até informações sobre Receitas Digitais',
        };
        suggestions = ['Entrar no Plantão 24h', 'Fazer Login'];
      } else if (lower.includes('preco') || lower.includes('preço') || lower.includes('valor') || lower.includes('quanto') || lower.includes('custo')) {
        replyText = 'Preços transparentes e sem mensalidade: Telemedicina por vídeo a partir de R$ 79 (Plantão R$ 89), Consulta Presencial R$ 150 e Médico em Casa R$ 400. Pagamento via PIX ou cartão em até 3x sem juros.';
        plannedAction = {
          type: 'scroll',
          target: '#especialidades',
          label: 'Exibindo valores das consultas',
        };
        suggestions = ['Plantão 24h Imediato', 'Ver Especialidades'];
      } else {
        replyText = 'Posso te ajudar a encontrar médicos por especialidade, chamar atendimento imediato no plantão 24h ou solicitar médico em casa. O que prefere fazer?';
        plannedAction = {
          type: 'scroll',
          target: '#especialidades',
          label: 'Rolando até as especialidades',
        };
        suggestions = ['Plantão 24h Imediato', 'Buscar Clínico Geral', 'Preços das Consultas'];
      }
    }

    const agentMsg: AgentMessage = {
      id: (Date.now() + 1).toString(),
      sender: 'agent',
      text: replyText,
      time: new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
      actionExecuted: plannedAction,
      actionStatus: 'executing',
      suggestions,
    };

    setMessages((prev) => [...prev, agentMsg]);
    setIsAgentBusy(false);

    // Execute in-page GUI action
    if (plannedAction) {
      await executeInPageAction(plannedAction);
      agentMsg.actionStatus = 'completed';
      setMessages((prev) => [...prev]);
    }
  };

  const handleReset = () => {
    setMessages([
      {
        id: 'reset',
        sender: 'agent',
        text: 'Histórico reiniciado! Em que posso te ajudar a navegar e encontrar agora?',
        time: new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
        suggestions: quickPrompts.slice(0, 4).map((p) => p.label),
      },
    ]);
  };

  return (
    <div ref={containerRef} className={cn('fixed bottom-6 right-4 sm:right-6 z-[100]', className)}>
      {/* ============================================================
          IN-PAGE GUI AGENT CHAT PANEL (Baseada no Alibaba Page Agent)
          ============================================================ */}
      {isOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="agent-dialog-title"
          className="absolute bottom-16 right-0 bg-white text-slate-800 rounded-3xl w-[calc(100vw-2rem)] sm:w-[410px] max-w-sm sm:max-w-md shadow-2xl shadow-blue-950/20 border border-slate-200/90 backdrop-blur-xl animate-in fade-in zoom-in-95 duration-200 overflow-hidden flex flex-col h-[540px] max-h-[85vh]"
        >
          {/* Header do Agente */}
          <div className="p-4 bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 text-white border-b border-blue-800/40 flex items-center justify-between shrink-0 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-10 h-10 rounded-2xl bg-white/20 border border-white/30 flex items-center justify-center text-white shadow-md">
                  <Bot className="w-5 h-5" />
                </div>
                <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-400 rounded-full border-2 border-blue-700 animate-pulse" />
              </div>

              <div>
                <div className="flex items-center gap-1.5">
                  <h3 id="agent-dialog-title" className="text-sm font-extrabold text-white">
                    Assistente Medikei
                  </h3>
                  <span className="text-[10px] font-bold px-1.5 py-0.2 rounded-md bg-white/20 text-cyan-200 border border-white/30">
                    {userRole === 'DOCTOR' ? 'Copiloto Médico' : userRole === 'PATIENT' ? 'Fale com o Medí' : 'Navegador IA'}
                  </span>
                </div>
                <p className="text-[11px] text-blue-100">
                  {userRole === 'DOCTOR' ? 'Automação clínica e controle de página' : 'Controla a tela e localiza dados por você'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={handleReset}
                className="p-1.5 rounded-lg text-blue-200 hover:text-white hover:bg-white/15 transition-colors cursor-pointer"
                title="Reiniciar conversa"
                aria-label="Reiniciar conversa"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="p-1.5 rounded-lg text-blue-200 hover:text-white hover:bg-white/15 transition-colors cursor-pointer"
                title="Fechar assistente"
                aria-label="Fechar assistente"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Activity Status Bar (Page Agent Real-time Feedback) */}
          {agentActivity && (
            <div className="px-4 py-1.5 bg-blue-50 border-b border-blue-100 text-[11px] text-blue-700 flex items-center gap-2 shrink-0 animate-pulse font-medium">
              <Sparkles className="w-3.5 h-3.5 text-blue-600 shrink-0" />
              <span className="truncate font-semibold">{agentActivity}</span>
            </div>
          )}

          {/* Messages Log */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3.5 text-xs bg-slate-50/70">
            {messages.map((msg) => {
              const isAgent = msg.sender === 'agent';
              return (
                <div
                  key={msg.id}
                  className={cn('flex flex-col', isAgent ? 'items-start' : 'items-end')}
                >
                  <div
                    className={cn(
                      'max-w-[90%] rounded-2xl p-3.5 leading-relaxed shadow-xs',
                      isAgent
                        ? 'bg-white border border-slate-200 text-slate-800 rounded-tl-xs shadow-xs'
                        : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-tr-xs shadow-xs'
                    )}
                  >
                    <p>{msg.text}</p>

                    {/* Badge de Ação Executada na Interface */}
                    {msg.actionExecuted && (
                      <div className="mt-2.5 pt-2 border-t border-slate-100 flex items-center justify-between text-[11px] text-blue-600 font-semibold">
                        <span className="flex items-center gap-1.5">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                          <span>{msg.actionExecuted.label}</span>
                        </span>
                      </div>
                    )}

                    <span
                      className={cn(
                        'text-[10px] block mt-1.5 opacity-70',
                        isAgent ? 'text-slate-400' : 'text-blue-100'
                      )}
                    >
                      {msg.time}
                    </span>
                  </div>

                  {/* Sugestões de Ação Rápida */}
                  {msg.suggestions && msg.suggestions.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5 max-w-[95%]">
                      {msg.suggestions.map((sug) => (
                        <button
                          key={sug}
                          type="button"
                          onClick={() => handleUserSubmit(sug)}
                          className="px-2.5 py-1 rounded-xl bg-white hover:bg-blue-600 border border-slate-200 hover:border-blue-600 text-[11px] font-semibold text-slate-700 hover:text-white transition-all cursor-pointer shadow-2xs hover:scale-[1.02] active:scale-95"
                        >
                          {sug}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {isAgentBusy && (
              <div className="flex items-center gap-2 p-3 bg-white rounded-2xl border border-slate-200 w-fit text-slate-600 text-xs shadow-xs">
                <span className="w-2 h-2 rounded-full bg-blue-600 animate-bounce" />
                <span className="w-2 h-2 rounded-full bg-blue-600 animate-bounce [animation-delay:0.2s]" />
                <span className="w-2 h-2 rounded-full bg-blue-600 animate-bounce [animation-delay:0.4s]" />
                <span className="text-[11px] font-semibold text-blue-700 ml-1">
                  Agente interagindo com a tela...
                </span>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Quick Prompts Bar */}
          <div className="px-3 py-2 bg-slate-100/90 border-t border-slate-200 shrink-0 overflow-x-auto no-scrollbar flex items-center gap-1.5">
            <span className="text-[10px] uppercase font-bold text-slate-500 shrink-0 mr-1">
              Atalhos:
            </span>
            {quickPrompts.map((qp) => (
              <button
                key={qp.label}
                type="button"
                onClick={() => handleUserSubmit(qp.query)}
                className="px-2.5 py-1 rounded-lg bg-white hover:bg-blue-50 text-slate-700 hover:text-blue-700 text-[11px] font-semibold transition-colors shrink-0 whitespace-nowrap border border-slate-200/90 shadow-2xs cursor-pointer hover:border-blue-300"
              >
                {qp.label}
              </button>
            ))}
          </div>

          {/* Input Field Form */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleUserSubmit();
            }}
            className="p-3 bg-white border-t border-slate-200 flex items-center gap-2 shrink-0"
          >
            <input
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder="Digite o que precisa (ex: 'ver receitas', 'plantão')..."
              className="flex-1 bg-slate-50 border border-slate-300 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 focus:bg-white transition-all"
            />
            <button
              type="submit"
              disabled={!inputText.trim() || isAgentBusy}
              className="p-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer shadow-md shadow-blue-500/20 active:scale-95"
              aria-label="Enviar comando para o agente"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}

      {/* ============================================================
          FLOATING TRIGGER BUTTON (Sofisticado com Badge de IA Ativa)
          ============================================================ */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex items-center gap-2.5 px-4 sm:px-5 py-3 rounded-full transition-all duration-300 cursor-pointer',
          'bg-gradient-to-r from-blue-600 via-primary to-indigo-600 text-white',
          'hover:from-blue-500 hover:to-indigo-500 active:scale-95',
          'shadow-xl shadow-primary/35 border-2 border-white/25',
          'hover:shadow-2xl hover:shadow-primary/50 hover:-translate-y-1',
          isOpen ? 'ring-4 ring-blue-400/40' : ''
        )}
        aria-expanded={isOpen}
        aria-label="Abrir assistente inteligente da página"
      >
        <div className="relative">
          <Sparkles className="w-4 h-4 text-cyan-200 animate-spin [animation-duration:8s]" />
          <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
        </div>
        <span className="font-extrabold text-xs sm:text-sm tracking-wide">
          {isOpen
            ? 'Fechar Assistente'
            : userRole === 'DOCTOR'
            ? 'Copiloto Medí'
            : 'Fale com o Medí'}
        </span>
      </button>
    </div>
  );
}
